import {
    MouseEventHandler,
    ReactElement,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';

import { cn } from '@/lib/cn';

type DropdownPlacement =
    | 'bottom-end'
    | 'bottom-start'
    | 'right-start'
    | 'right-end';

type DropdownOpenBehavior = 'hover' | 'click';

type TriggerRenderProps = {
    open: boolean;
    triggerProps: {
        'aria-expanded': boolean;
        'aria-haspopup': 'menu';
        onClick?: MouseEventHandler<HTMLElement>;
    };
};

type DropdownProps = {
    renderTrigger: (props: TriggerRenderProps) => ReactElement;
    children: ReactNode;
    openBehavior?: DropdownOpenBehavior;
    isOpen?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    closeOnContentClick?: boolean;
    placement?: DropdownPlacement;
    className?: string;
};

const placementClassNameMap: Record<DropdownPlacement, string> = {
    'bottom-end': 'top-full right-0 pt-1',
    'bottom-start': 'top-full left-0 pt-1',
    'right-start': 'top-0 left-full pl-1',
    'right-end': 'top-0 right-full pr-1',
};

export const Dropdown = ({
    renderTrigger,
    children,
    openBehavior = 'hover',
    isOpen,
    defaultOpen = false,
    onOpenChange,
    closeOnContentClick = false,
    placement = 'bottom-end',
    className,
}: DropdownProps) => {
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const containerRef = useRef<HTMLDivElement>(null);

    const open = isOpen ?? internalOpen;

    const setOpen = (nextOpen: boolean) => {
        if (isOpen === undefined) {
            setInternalOpen(nextOpen);
        }

        onOpenChange?.(nextOpen);
    };

    useEffect(() => {
        if (openBehavior !== 'click' || !open) {
            return;
        }

        const handleMouseDown = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, openBehavior]);

    const triggerProps = {
        'aria-expanded': open,
        'aria-haspopup': 'menu' as const,
        ...(openBehavior === 'click'
            ? {
                  onClick: ((event) => {
                      event.stopPropagation();
                      setOpen(!open);
                  }) as MouseEventHandler<HTMLElement>,
              }
            : {}),
    };

    return (
        <div
            ref={containerRef}
            className={cn('relative', className)}
            onMouseEnter={
                openBehavior === 'hover' ? () => setOpen(true) : undefined
            }
            onMouseLeave={
                openBehavior === 'hover' ? () => setOpen(false) : undefined
            }
        >
            {renderTrigger({ open, triggerProps })}

            <div
                className={cn(
                    'absolute z-50 transition-all duration-150',
                    placementClassNameMap[placement],
                    open
                        ? 'visible opacity-100'
                        : 'pointer-events-none invisible opacity-0',
                )}
                onClick={closeOnContentClick ? () => setOpen(false) : undefined}
            >
                <div className="rounded-lg bg-white shadow-md">{children}</div>
            </div>
        </div>
    );
};
